import React, { useState, useCallback } from "react"
import {
  Box, HStack, Text,
} from "@chakra-ui/react"
import FullScreenModalNav from "components/dialogs/FullScreenModalNav"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import Modal from "components/dialogs/Modal"
import { BuilderBlockWithBlockId, Website } from "sharedTypes"
import { DraggableItem } from "sharedTypes/builder"
import BuilderLayout from "components/Builder/BuilderLayout"
import EditorPanel from "components/Builder/EditorPanel/EditorPanel"
import {
  handleAddBlock, handleBlockContentChange, handleDeleteBlock, handleMove,
  mapBlocksToBuilderBlocksWithId,
} from "components/Builder/helpers"
import { RichTextFieldType } from "components/ReactHookForm/RichTextField"
import BuildingBlock, { BlockInsertPosition } from "components/Builder/BuildingBlock"
import BlockContent from "components/Builder/BlockContent"
import BuilderContentArea from "components/Builder/BuilderContentArea"
import { eventWebsiteUploadsUrl } from "api/websites"
import EditPageContentModalSettingsPanel from "pages/EventShow/WebPage/EditPageContentModalSettingsPanel"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"

interface Props {
  eventId: string
  website: Website
  onHide: () => void
}

const WebpageBuilder = ({ eventId, website, onHide }: Props) => {
  const queryClient = useQueryClient()
  const [blocks, setBlocks] = useState<BuilderBlockWithBlockId[]>(
    mapBlocksToBuilderBlocksWithId(website.websiteData?.blocks || []),
  )
  const [selectedBlockId, setSelectedBlockId] = useState<number>()

  const selectedBlock = blocks.find(({ blockId }) => blockId === selectedBlockId)

  const handleSaveForm = async () => {
    await api.putWebsite(eventId, {
      websiteData: { blocks: blocks.map(({ block }) => block) },
    })
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        onHide()
      })
  }

  const handleCancel = () => {
    setBlocks(mapBlocksToBuilderBlocksWithId(website.websiteData?.blocks || []))
    onHide()
  }

  const handleDropIntoDropHereBox = (item: DraggableItem) => {
    if (item.blockTypeToAdd) {
      handleAddBlock({
        type: item.blockTypeToAdd,
        setBlocks,
        setSelectedBlockId,
        position: BlockInsertPosition.Bottom,
        blockId: 0,
      })
    }
  }

  const handleDuplicateBlock = (blockId: number) => {
    const blockToDuplicate = blocks.find((block) => block.blockId === blockId)

    if (!blockToDuplicate) {
      return
    }

    handleAddBlock({
      type: blockToDuplicate.block.type,
      setBlocks,
      setSelectedBlockId,
      position: BlockInsertPosition.Bottom,
      blockId,
      block: blockToDuplicate.block,
    })
  }

  const handleDropBlock = useCallback((
    item: DraggableItem,
    blockId: number,
    position: BlockInsertPosition,
  ) => {
    if (item.blockTypeToAdd) {
      handleAddBlock({
        type: item.blockTypeToAdd,
        setBlocks,
        setSelectedBlockId,
        position,
        blockId,
      })
    }
  }, [setBlocks, setSelectedBlockId])

  return (
    <Modal
      show
      size="full"
      hide={handleCancel}
      closeOnEsc={false}
      header={(
        <FullScreenModalNav type="header">
          <Text w={1 / 3}>Web Page Builder</Text>
          <HStack spacing={8} w={1 / 3} justify="flex-end">
            <Link onClick={handleCancel} color="gray.500" fontWeight="bold">
              Cancel
            </Link>
            <Link onClick={handleSaveForm} color="blue.300" fontWeight="bold">
              Save and Close
              <Icon icon="cancel-circle" ml={2} />
            </Link>
          </HStack>
        </FullScreenModalNav>
      )}
      noPadding
    >
      <BuilderLayout
        sidePanel={(
          <>
            {selectedBlock ? (
              <EditorPanel
                selectedBlock={selectedBlock?.block}
                onChange={(data) => handleBlockContentChange(data, setBlocks, selectedBlockId)}
                onCancel={() => setSelectedBlockId(undefined)}
                selectedBlockId={selectedBlockId ?? 0}
                imageUploadUrl={eventWebsiteUploadsUrl(eventId)}
                onDeleteBlock={() => handleDeleteBlock(setBlocks, selectedBlockId)}
                richTextFieldType={RichTextFieldType.WEBSITE}
                showMergeTags={false}
              />
            ) : (
              <EditPageContentModalSettingsPanel
                onAttributeChange={() => {}}
              />
            )}
          </>
        )}
        handleDropIntoDropHereBox={handleDropIntoDropHereBox}
        showDropHereBox={blocks.length === 0}
      >
        <BuilderContentArea onBackdropClick={() => setSelectedBlockId(undefined)}>
          <Box borderWidth={1}>
            {blocks.map(({ block, blockId }) => (
              <BuildingBlock
                key={blockId}
                dragId={blockId}
                onMove={(
                  fromDragId: number,
                  toDragId: number,
                  position: BlockInsertPosition,
                ) => handleMove(blocks, setBlocks, fromDragId, toDragId, position)}
                onSelect={() => setSelectedBlockId(blockId)}
                selected={selectedBlock?.blockId === blockId}
                onDuplicate={() => handleDuplicateBlock(blockId)}
                onDelete={() => handleDeleteBlock(setBlocks, blockId)}
                type={block.type}
                onDropInto={handleDropBlock}
              >
                <BlockContent block={block} hasPadding />
              </BuildingBlock>
            ))}
          </Box>
        </BuilderContentArea>
      </BuilderLayout>
    </Modal>
  )
}

export default WebpageBuilder
